import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

import { MobileAwareWebsiteLink } from 'components/MobileAwareWebsiteLink/MobileAwareWebsiteLink';

import { getListingAmenities } from 'helpers/getListingAmenities';
import { normalizeUrl } from 'helpers/normalizeUrl';

import type { DirectoryCardFields } from 'hooks/useDirectoryConfig/types';
import type { FreeHotelItem } from 'hooks/useDirectoryData/types';

import { htmlToPlainText } from 'views/DirectoryView/helpers/htmlToPlainText';

const ALWAYS_VISIBLE_KEYS: (keyof FreeHotelItem)[] = ['company'];

// Fields must be listed explicitly to render at all; the order here is the order on the card.
const TEXT_FIELD_KEYS: (keyof FreeHotelItem)[] = [
  'category',
  'type_name',
  'cat_name',
  'sub_catname',
  'cuisine',
  'rank_name',
  'number_of_rooms',
  'description',
  'description_2',
  'description_3',
  'addr1',
  'addr2',
  'addr3',
  'city',
  'state',
  'zip',
  'country',
  'region',
  'primary_contact_full_name',
  'primary_contact_title',
  'phone',
  'alt_phone',
  'toll_free',
  'fax',
  'email',
  'web_url',
  'instagram',
  'notes',
  'official_name',
];

const HTML_FIELD_KEYS: (keyof FreeHotelItem)[] = ['description', 'description_2', 'description_3'];

// `brand` and `segment` aren't listed: they render as the top and bottom logo images instead.

export interface FreeHotelProps {
  id: string;
  flyToMarker: () => void;
  hotelInfo: () => void;
  hotelName: string;
  hotel: FreeHotelItem;
  cardFields: DirectoryCardFields;
  brandImageUrl?: string | null;
  segmentImageUrl?: string | null;
}

export function FreeHotel({
  id,
  flyToMarker,
  hotelInfo,
  hotelName,
  hotel,
  cardFields,
  brandImageUrl,
  segmentImageUrl,
}: FreeHotelProps) {
  const textFields = [
    ...ALWAYS_VISIBLE_KEYS,
    ...TEXT_FIELD_KEYS.filter((key) => {
      return !ALWAYS_VISIBLE_KEYS.includes(key) && cardFields[key];
    }),
  ];
  const tags = getListingAmenities(hotel).filter(({ column }) => {
    return cardFields[column];
  });

  return (
    <div className="freeDiv" id={id}>
      <div className="iconDiv">
        <span
          className="fa-layers fa-fw fa-2x markerCircle"
          onClick={flyToMarker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              flyToMarker();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={i18n.translate('directoryView.viewOnMap') as string}
          key="map"
        >
          <FontAwesomeIcon icon={['fas', 'circle']} className="circle" />
          <FontAwesomeIcon icon={['fas', 'map-marker-alt']} className="marker" />
        </span>
      </div>
      <div className="freeInfoDiv" onClick={hotelInfo}>
        <div className="content">
          {cardFields.brand && brandImageUrl && (
            <img className="freeHotelImg logo-top" src={brandImageUrl} alt={`${hotelName} brand logo`} />
          )}
          {textFields.map((key) => {
            const rawHotelParam = hotel[key];
            // Fixes a typing issue by explicitly only allowing plain values to be shown as card text. Excludes objects and arrays (e.g. the `images`).
            // Functionally does not change anything (yet) as only text fields are being included in the list for now anyway.
            if (typeof rawHotelParam !== 'string' && typeof rawHotelParam !== 'number') return null;

            const value = HTML_FIELD_KEYS.includes(key) ? htmlToPlainText(rawHotelParam.toString()) : rawHotelParam;
            if (!value) return null;
            if (key === 'web_url') {
              return (
                // The card opens the profile on click. This span stops that so the link only opens the website.
                <span
                  key={key}
                  data-field={key}
                  className={clsx('free-hotel-detail', 'free-hotel-detail--web_url')}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <MobileAwareWebsiteLink url={normalizeUrl(value.toString())}>
                    {i18n.translate('directoryView.viewWebsite')}
                  </MobileAwareWebsiteLink>
                </span>
              );
            }
            return (
              <span
                key={key}
                data-field={key}
                className={clsx('free-hotel-detail', `free-hotel-detail--${key}`, {
                  freeHotelName: key === 'company', // here for legacy custom scss
                  freeHotelAddr: key === 'city', // here for legacy custom scss
                  'free-hotel-title': key === 'company',
                  'free-hotel-detail--new-section': key === 'city',
                  'free-hotel-detail--small': key === 'sub_catname',
                  'free-hotel-detail--subtitle': key === 'primary_contact_title',
                  'free-hotel-detail--muted': key === 'notes' || key === 'official_name',
                })}
              >
                {value} <br />
              </span>
            );
          })}
          {tags.length > 0 && (
            <div className="freeHotelTags">
              {tags.map(({ column, label }) => {
                return (
                  <span
                    key={column}
                    data-field={column}
                    className={clsx('free-hotel-tag', `free-hotel-tag--${column}`)}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          )}
          {cardFields.segment && segmentImageUrl && (
            <img className="freeHotelImg logo-bottom" src={segmentImageUrl} alt={`${hotelName} segment logo`} />
          )}
          <br />
          <span className="moreInfo">{i18n.translate('directoryView.viewNow')}</span>
          <br />
          <br />
        </div>
      </div>
    </div>
  );
}
